import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  TrendingUp,
  Layers,
  Award,
  Users,
  UserCheck,
  PlayCircle,
  BookOpen,
  Headphones,
  Badge,
  Beaker,
  CheckCircle2,
  Compass,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/politics/Header";
import Footer from "@/components/politics/Footer";
import AgencyLeadForm from "@/components/politics/AgencyLeadForm";

/* ============================================================= */
/*  1 — Hero                                                     */
/* ============================================================= */
function AgencyHero() {
  return (
    <section
      data-testid="agency-hero"
      className="relative isolate overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28"
    >
      <div className="absolute inset-0 -z-10 rsm-grid-bg opacity-90" />
      <div
        className="absolute -top-32 left-[-10%] -z-10 h-[520px] w-[520px] rounded-full blur-3xl opacity-30"
        style={{
          background: "radial-gradient(closest-side, #7E20FF, transparent 70%)",
        }}
      />
      <div
        className="absolute -bottom-32 right-[-10%] -z-10 h-[420px] w-[420px] rounded-full blur-3xl opacity-25"
        style={{
          background: "radial-gradient(closest-side, #208BFF, transparent 70%)",
        }}
      />

      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-4xl rsm-rise">
          <div
            data-testid="agency-eyebrow"
            className="inline-flex items-center gap-2 rounded-full border border-[#1B1D1D]/10 bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-[#1B1D1D]"
          >
            <Sparkles size={12} className="text-[#7E20FF]" />
            Programa de Parceiros · /politics/agencies
          </div>

          <h1
            data-testid="agency-headline"
            className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.94] text-[#1B1D1D]"
          >
            Transforme sua agência no{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-[#7E20FF]">
                cérebro de base
              </span>
              <span className="absolute inset-x-0 bottom-1 h-3 -z-0 bg-[#7E20FF]/15 rounded" />
            </span>{" "}
            das campanhas que você atende.
          </h1>

          <p
            data-testid="agency-subhead"
            className="mt-8 max-w-3xl text-lg sm:text-xl leading-relaxed text-[#51565F]"
          >
            Com a RSM Politics, sua agência (ou consultoria) passa a oferecer
            programas de base e inteligência contínua — além de comunicação.
            Mais valor para o cliente, mais ticket para você.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a
              href="#parceria"
              data-testid="agency-hero-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#7E20FF] px-7 py-4 text-base font-semibold text-white shadow-sm transition-all duration-300 ease-out hover:bg-[#6818D6] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#7E20FF] focus:ring-offset-2 focus:ring-offset-[#F5FAFB]"
            >
              Quero ser parceiro RSM Politics
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
            <Link
              to="/politics"
              data-testid="agency-hero-secondary"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1B1D1D]/15 bg-white/70 backdrop-blur px-7 py-4 text-base font-medium text-[#1B1D1D] transition-all duration-300 ease-out hover:border-[#208BFF] hover:text-[#208BFF]"
            >
              Ver como funciona para QGs
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  2 — Why                                                       */
/* ============================================================= */
const WHY_POINTS = [
  {
    title: "Campanhas que ganham têm base",
    desc: "Apoiadores organizados, ativos e medidos — esse é o terreno em que a eleição realmente é decidida.",
  },
  {
    title: "Poucas agências entregam isso",
    desc: "A maioria do mercado ainda opera só com peças, mídia e relacionamento. Base + dados é o gap.",
  },
  {
    title: "RSM Politics te coloca no centro",
    desc: "Plataforma e método para a sua agência liderar a transformação que as campanhas vão exigir em 2026.",
  },
];

function WhySection() {
  return (
    <section
      data-testid="agency-why"
      className="relative py-24 sm:py-32 border-t border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#7E20FF]">
            Por que parceria
          </span>
          <h2
            data-testid="agency-why-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Sua expertise política +{" "}
            <span className="text-[#7E20FF]">a infraestrutura</span> que falta no
            mercado.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#51565F] max-w-md">
            Esta não é uma venda de software. É um modelo de parceria pensado
            para agências e consultores que querem operar campanhas modernas com
            base, dados e inteligência contínua — não substituí-los.
          </p>
        </div>

        <div className="lg:col-span-7 space-y-4">
          {WHY_POINTS.map((p, i) => (
            <div
              key={p.title}
              data-testid={`agency-why-card-${i}`}
              className="group rounded-2xl border border-[#1B1D1D]/8 bg-white p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#7E20FF]/40"
            >
              <div className="flex items-start gap-5">
                <span className="font-display font-bold text-3xl text-[#7E20FF]/40 group-hover:text-[#7E20FF] transition-colors">
                  0{i + 1}
                </span>
                <div>
                  <h3 className="font-display text-xl font-semibold text-[#1B1D1D]">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-[#51565F]">
                    {p.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  3 — How partnership works                                    */
/* ============================================================= */
const HOW_BLOCKS = [
  {
    n: "01",
    title: "Você traz sua expertise política",
    desc: "Conhecimento de território, leitura de narrativa, posicionamento estratégico e a relação direta com o cliente. O DNA da sua agência continua sendo o seu DNA.",
    color: "#208BFF",
  },
  {
    n: "02",
    title: "A RSM traz a infraestrutura de base e dados",
    desc: "Plataforma para cadastrar apoiadores, distribuir missões, rodar pesquisas internas e ler resultados em dashboards por perfil.",
    color: "#7E20FF",
  },
  {
    n: "03",
    title: "Juntos, vocês entregam um novo serviço",
    desc: "Programa de Base e Inteligência Contínua, que complementa mídia, criação e comunicação. Maior valor, maior ticket, maior diferenciação.",
    color: "#3ED383",
  },
];

function HowSection() {
  return (
    <section
      id="como-funciona"
      data-testid="agency-how"
      className="relative py-24 sm:py-32 bg-white border-y border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#208BFF]">
            Como funciona a parceria
          </span>
          <h2
            data-testid="agency-how-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Três peças. Um serviço novo no mercado.
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {HOW_BLOCKS.map((block, i) => (
            <article
              key={block.n}
              data-testid={`agency-how-block-${i}`}
              className="group relative overflow-hidden rounded-2xl border border-[#1B1D1D]/8 bg-[#F5FAFB] p-8 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md"
            >
              <span
                className="font-mono text-sm font-semibold"
                style={{ color: block.color }}
              >
                {block.n}
              </span>
              <h3 className="mt-3 font-display text-2xl font-semibold leading-tight text-[#1B1D1D]">
                {block.title}
              </h3>
              <p className="mt-4 text-[15px] leading-relaxed text-[#51565F]">
                {block.desc}
              </p>
              <div
                className="mt-7 h-1 w-10 rounded-full transition-all duration-300 group-hover:w-20"
                style={{ background: block.color }}
              />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  4 — Benefits for agencies                                    */
/* ============================================================= */
const BENEFITS = [
  {
    icon: TrendingUp,
    title: "Nova linha de receita",
    desc: "Cobre pelo desenho e operação de programas de base com RSM — fees próprios mais a sua margem habitual.",
    accent: "#3ED383",
  },
  {
    icon: Layers,
    title: "Oferta mais robusta",
    desc: "Além de peças, você entrega dashboards, insights e relatórios contínuos de engajamento e base.",
    accent: "#208BFF",
  },
  {
    icon: Award,
    title: "Diferenciação competitiva",
    desc: "Posicione sua agência como dona de base + dados, não apenas de criação. Saia do leilão de preço.",
    accent: "#7E20FF",
  },
  {
    icon: Users,
    title: "Indicações da RSM (2028+)",
    desc: "No médio prazo, a RSM pretende indicar agências parceiras para campanhas que buscarem operação especializada.",
    accent: "#FA9F22",
  },
];

function BenefitsSection() {
  return (
    <section
      id="beneficios"
      data-testid="agency-benefits"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#7E20FF]">
            Benefícios para agências
          </span>
          <h2
            data-testid="agency-benefits-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            O que muda no seu negócio.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <article
                key={b.title}
                data-testid={`agency-benefit-${i}`}
                className="group relative overflow-hidden rounded-2xl border border-[#1B1D1D]/8 bg-white p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#1B1D1D]/15"
              >
                <div
                  className="grid h-11 w-11 place-items-center rounded-xl"
                  style={{
                    backgroundColor: `${b.accent}15`,
                    color: b.accent,
                  }}
                >
                  <Icon size={22} />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-[#1B1D1D]">
                  {b.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#51565F]">
                  {b.desc}
                </p>
                <span
                  className="absolute -bottom-12 -right-12 h-32 w-32 rounded-full opacity-0 transition-opacity duration-500 group-hover:opacity-15"
                  style={{ background: b.accent }}
                />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  5 — Consultants role                                         */
/* ============================================================= */
function ConsultantsSection() {
  return (
    <section
      data-testid="agency-consultants"
      className="relative py-24 sm:py-32 bg-[#1B1D1D] text-[#F5FAFB] overflow-hidden border-y border-[#1B1D1D]/8"
    >
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #F5FAFB 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />
      <div
        className="absolute -top-32 right-[-10%] h-[420px] w-[420px] rounded-full blur-3xl opacity-25"
        style={{
          background: "radial-gradient(closest-side, #208BFF, transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#208BFF]">
            Para consultores independentes
          </span>
          <h2
            data-testid="agency-consultants-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[0.98] text-[#F5FAFB]"
          >
            Estrategista certificado, não só conselheiro.
          </h2>
          <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-[#F5FAFB]/75">
            Consultores e marqueteiros independentes podem se certificar como{" "}
            <span className="text-[#F5FAFB] font-medium">
              Operadores RSM Politics
            </span>{" "}
            — sendo contratados por agências ou QGs para implementar programas
            de base usando a plataforma.
          </p>
          <ul className="mt-8 space-y-3 max-w-2xl">
            {[
              "Entregue estratégia + execução + leitura de dados — não só uma apresentação.",
              "Aumente o seu ticket e o seu papel dentro das operações.",
              "Atue em parceria com agências sem perder protagonismo.",
            ].map((line, i) => (
              <li
                key={line}
                className="flex items-start gap-3 text-[15px] leading-relaxed text-[#F5FAFB]/85"
              >
                <UserCheck
                  size={18}
                  className="mt-0.5 text-[#208BFF] flex-shrink-0"
                />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-5">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-8">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#208BFF]/15 text-[#208BFF]">
              <Award size={22} />
            </div>
            <h3 className="mt-5 font-display text-xl font-semibold text-[#F5FAFB]">
              Selo Operador RSM Politics
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-[#F5FAFB]/70">
              Reconhecimento profissional para uso em propostas, perfil e
              materiais comerciais, conforme o programa for sendo formalizado.
            </p>
            <div className="mt-5 flex items-center gap-2 text-xs text-[#7E20FF]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#7E20FF] animate-pulse" />
              Em formalização — primeiras turmas em 2026
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  6 — What RSM offers in the partnership                       */
/* ============================================================= */
const OFFERS = [
  {
    icon: PlayCircle,
    title: "Ambiente de demonstração",
    desc: "Acesso a um ambiente de testes para conhecer a plataforma e mostrar para os clientes da agência.",
  },
  {
    icon: BookOpen,
    title: "Playbooks e templates",
    desc: "Templates de missões, fluxos de implantação e materiais prontos para acelerar a operação de campanha.",
  },
  {
    icon: Beaker,
    title: "Treinamento inicial",
    desc: "Workshop de onboarding e sessões online para a equipe da agência ou para o consultor.",
  },
  {
    icon: Headphones,
    title: "Suporte prioritário",
    desc: "Contato dedicado durante o ciclo eleitoral, com tempo de resposta acelerado para parceiros.",
  },
  {
    icon: Badge,
    title: "Selo de parceiro",
    desc: "Reconhecimento como Parceiro RSM Politics conforme o programa for sendo formalizado.",
  },
];

function OffersSection() {
  return (
    <section
      data-testid="agency-offers"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#208BFF]">
            O que a RSM oferece
          </span>
          <h2
            data-testid="agency-offers-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            O que você recebe ao entrar no programa.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#51565F] max-w-xl">
            O programa segue em formalização. Os itens abaixo são compromissos
            estruturantes — o detalhamento final é apresentado na conversa de
            alinhamento.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          {OFFERS.map((o, i) => {
            const Icon = o.icon;
            const span =
              i === 0 ? "lg:col-span-3" : i === 4 ? "lg:col-span-3" : "lg:col-span-2";
            return (
              <article
                key={o.title}
                data-testid={`agency-offer-${i}`}
                className={`${span} group rounded-2xl border border-[#1B1D1D]/8 bg-white p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#1B1D1D]/15`}
              >
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#208BFF]/12 text-[#208BFF]">
                  <Icon size={22} />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-[#1B1D1D]">
                  {o.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#51565F]">
                  {o.desc}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  7 — Steps to join                                            */
/* ============================================================= */
const STEPS = [
  {
    n: "01",
    title: "Enviar interesse",
    desc: "Preencha o formulário com tipo de atuação (agência ou consultor), região e perfil dos clientes.",
  },
  {
    n: "02",
    title: "Conversa de alinhamento",
    desc: "Reunião para conhecer o seu trabalho e entender o fit com os programas RSM Politics.",
  },
  {
    n: "03",
    title: "Primeiro piloto",
    desc: "Implementação da plataforma em uma campanha real em 2026, com suporte próximo do time RSM.",
  },
];

function JoinSection() {
  return (
    <section
      data-testid="agency-join"
      className="relative py-24 sm:py-32 bg-white border-y border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#7E20FF]">
            Como entrar
          </span>
          <h2
            data-testid="agency-join-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Três passos para fazer parte da primeira leva.
          </h2>
        </div>

        <ol className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
          {STEPS.map((step, i) => (
            <li
              key={step.n}
              data-testid={`agency-step-${i}`}
              className="relative rounded-2xl border border-[#1B1D1D]/8 bg-[#F5FAFB] p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:border-[#7E20FF]/40"
            >
              <span className="font-mono text-sm font-semibold text-[#7E20FF]">
                {step.n}
              </span>
              <h3 className="mt-3 font-display text-xl font-semibold text-[#1B1D1D]">
                {step.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#51565F]">
                {step.desc}
              </p>
              {i < STEPS.length - 1 && (
                <span className="hidden md:block absolute top-1/2 right-[-14px] -translate-y-1/2 text-[#1B1D1D]/15">
                  <ArrowRight size={20} />
                </span>
              )}
            </li>
          ))}
        </ol>

        <div className="mt-14 text-center">
          <a
            href="#parceria"
            data-testid="agency-join-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#7E20FF] px-7 py-4 text-base font-semibold text-white transition-all duration-300 ease-out hover:bg-[#6818D6] hover:-translate-y-0.5"
          >
            Quero ser parceiro RSM Politics
            <ArrowRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  8 — Agency FAQ                                               */
/* ============================================================= */
const AGENCY_FAQS = [
  {
    q: "A parceria tem custo?",
    a: "O programa de parceiros está em formalização. A entrada no piloto não exige investimento financeiro antecipado por parte do parceiro — apenas o compromisso de implementar a RSM Politics em pelo menos uma campanha real em 2026, com acompanhamento próximo do nosso time.",
  },
  {
    q: "Como funciona a remuneração / comissão?",
    a: "A agência ou consultor cobra o seu próprio fee pelo desenho e operação do programa de base do cliente. Sobre a contratação da plataforma pelo cliente final, definimos o modelo de remuneração de parceiros caso a caso durante a conversa de alinhamento — o objetivo é que a parceria seja vantajosa para os dois lados.",
  },
  {
    q: "Preciso ter um tamanho mínimo de carteira?",
    a: "Não. Buscamos parceiros pela qualidade do trabalho, especialização em campanhas e fit com o método RSM Politics. Tanto agências consolidadas quanto consultores independentes com boa atuação são bem-vindos.",
  },
  {
    q: "Qual o nível de suporte da RSM durante as campanhas?",
    a: "Parceiros do programa têm suporte prioritário em todo o ciclo eleitoral — com tempo de resposta acelerado, contato dedicado e acompanhamento de implantação. O detalhamento é apresentado na reunião de alinhamento.",
  },
  {
    q: "Posso usar RSM com clientes comerciais no futuro?",
    a: "O foco atual da RSM Politics é política e campanhas. A discussão sobre uso em outros contextos (instituicional, sindicatos, federações etc.) é caso a caso e pode entrar no horizonte do programa conforme amadurecimento.",
  },
];

function AgencyFAQ() {
  return (
    <section
      id="faq"
      data-testid="agency-faq"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-4xl px-6">
        <div className="text-center">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#208BFF]">
            Perguntas frequentes
          </span>
          <h2
            data-testid="agency-faq-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            As principais dúvidas de quem quer ser parceiro.
          </h2>
        </div>

        <Accordion
          type="single"
          collapsible
          className="mt-12 divide-y divide-[#1B1D1D]/10 rounded-2xl border border-[#1B1D1D]/10 bg-white"
          data-testid="agency-faq-accordion"
        >
          {AGENCY_FAQS.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`item-${i}`}
              className="border-b-0 px-6"
              data-testid={`agency-faq-item-${i}`}
            >
              <AccordionTrigger className="text-left font-display text-lg font-semibold text-[#1B1D1D] hover:text-[#7E20FF] hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[15px] leading-relaxed text-[#51565F] pb-5">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <p className="mt-10 text-center text-sm text-[#7D8188] max-w-2xl mx-auto">
          Outras dúvidas? Envie pelo formulário abaixo — respondemos pessoalmente
          em até 5 dias úteis.
          <CheckCircle2
            size={14}
            className="inline-block ml-1 text-[#3ED383]"
          />
        </p>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  Page                                                          */
/* ============================================================= */
export default function AgenciesPage() {
  return (
    <main
      data-testid="agencies-page"
      className="min-h-screen bg-[#F5FAFB] text-[#1B1D1D]"
    >
      <Header />
      <AgencyHero />
      <WhySection />
      <HowSection />
      <BenefitsSection />
      <ConsultantsSection />
      <OffersSection />
      <DirectoryTeaserSection />
      <JoinSection />
      <AgencyFAQ />
      <AgencyLeadForm />
      <Footer />
    </main>
  );
}

/* ============================================================= */
/*  Directory teaser strip                                        */
/* ============================================================= */
function DirectoryTeaserSection() {
  return (
    <section
      data-testid="agency-directory-teaser"
      className="relative isolate py-20 sm:py-24 border-y border-[#1B1D1D]/8 overflow-hidden"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/3931512/pexels-photo-3931512.jpeg?auto=compress&cs=tinysrgb&w=1920&q=80)",
          zIndex: 0,
        }}
        aria-hidden="true"
      />
      {/* Color overlays — escurece e tinge com o roxo da vertical (#7E20FF) */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(115deg, rgba(27,29,29,0.88) 0%, rgba(27,29,29,0.72) 45%, rgba(126,32,255,0.55) 100%)",
          zIndex: 1,
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 rsm-grid-bg opacity-20 mix-blend-overlay"
        style={{ zIndex: 2 }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-7xl px-6" style={{ zIndex: 3 }}>
        <div className="rounded-3xl border border-white/15 bg-white/8 backdrop-blur-xl p-8 sm:p-12 overflow-hidden relative shadow-2xl">
          <div
            className="absolute -top-24 -right-24 h-72 w-72 rounded-full blur-3xl opacity-50 pointer-events-none"
            style={{
              background:
                "radial-gradient(closest-side, #7E20FF, transparent 70%)",
            }}
          />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase text-[#C9A8FF]">
                <Compass size={14} /> Diretório público
              </div>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tighter text-white">
                Parceiros certificados também ganham vitrine.
              </h2>
              <p className="mt-4 text-[15px] sm:text-base leading-relaxed text-white/80 max-w-2xl">
                Cada agência e consultor do programa entra no diretório público{" "}
                <span className="font-semibold text-white">
                  /politics/parceiros
                </span>
                . QGs que chegam à RSM procurando especialistas regionais ou
                temáticos descobrem você ali — prova social e geração de
                demanda do dia 1.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link
                to="/politics/parceiros"
                data-testid="agency-directory-cta"
                className="inline-flex items-center gap-2 rounded-full bg-[#7E20FF] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#6818D6] hover:-translate-y-0.5 transition-all duration-300 ease-out shadow-lg shadow-[#7E20FF]/30"
              >
                Ver diretório de parceiros
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
